/**
 * Google Places text search for Routy. Uses the Places library already loaded
 * for address autocomplete (PlacesService), so no new API/script is needed.
 *
 * This powers "find me a Starbucks near my route" / "which restaurants are
 * close to my starting address" style requests, including review/rating signal
 * and straight-line distance from a bias point.
 */
import { calculateDistance } from '../utils/geoDistance';
import { ensureGoogleMapsLoaded } from '../utils/googleMapsLoader';

export interface PlaceSearchResult {
  name: string;
  address: string;
  placeId: string;
  lat: number;
  lng: number;
  rating: number | null;
  reviewCount: number | null;
  /** Straight-line miles from the bias point, when one was provided. */
  distanceMiles: number | null;
  openNow: boolean | null;
  /** Storefront/photo URL from Google, when available. */
  photoUrl: string | null;
}

export interface PlaceReview {
  author: string;
  rating: number | null;
  text: string;
  relativeTime: string;
}

export interface PlaceDetails {
  placeId: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  rating: number | null;
  reviewCount: number | null;
  openNow: boolean | null;
  phone: string | null;
  website: string | null;
  mapsUrl: string | null;
  photoUrl: string | null;
  topReview: PlaceReview | null;
}

let cachedService: google.maps.places.PlacesService | null = null;

/**
 * Cache of the most recent search results keyed by placeId so present_places
 * can build cards (and reuse coordinates/photo) without re-searching.
 */
const placeCache = new Map<string, PlaceSearchResult>();

/** Look up a previously-searched place by id (used by present_places). */
export function getCachedPlace(placeId: string): PlaceSearchResult | undefined {
  return placeCache.get(placeId);
}

async function getService(): Promise<google.maps.places.PlacesService> {
  if (cachedService) return cachedService;
  // Self-loads the Maps script if this is called from a page that never
  // mounted a GooglePlacesAddress input (e.g. Routy opened from Settings).
  await ensureGoogleMapsLoaded();
  // PlacesService needs an attribution node; a detached div is fine for
  // background queries (we render our own results).
  cachedService = new google.maps.places.PlacesService(document.createElement('div'));
  return cachedService;
}

/**
 * Text search for places matching `query`, optionally biased toward and ranked
 * by distance from `bias` (e.g. the operator's starting address or an
 * appointment location).
 */
export async function searchPlaces(
  query: string,
  bias?: { lat: number; lng: number } | null,
  maxResults = 5,
  radiusMiles = 10,
): Promise<PlaceSearchResult[]> {
  const service = await getService();

  const request: google.maps.places.TextSearchRequest = { query };
  if (bias) {
    request.location = new google.maps.LatLng(bias.lat, bias.lng);
    request.radius = Math.round(radiusMiles * 1609.34);
  }

  const results = await new Promise<google.maps.places.PlaceResult[]>((resolve, reject) => {
    service.textSearch(request, (places, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && places) {
        resolve(places);
      } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        resolve([]);
      } else {
        reject(new Error(`Places search failed (${status}).`));
      }
    });
  });

  const mapped: PlaceSearchResult[] = results
    .filter((p) => !!p.geometry?.location)
    .map((p) => {
      const lat = p.geometry!.location!.lat();
      const lng = p.geometry!.location!.lng();
      return {
        name: p.name || 'Unknown',
        address: p.formatted_address || '',
        placeId: p.place_id || '',
        lat,
        lng,
        rating: typeof p.rating === 'number' ? p.rating : null,
        reviewCount: typeof p.user_ratings_total === 'number' ? p.user_ratings_total : null,
        distanceMiles: bias ? Number(calculateDistance(bias.lat, bias.lng, lat, lng).toFixed(1)) : null,
        openNow: p.opening_hours?.isOpen?.() ?? null,
        photoUrl: p.photos?.[0]?.getUrl({ maxWidth: 480, maxHeight: 280 }) ?? null,
      };
    });

  if (bias) {
    mapped.sort((a, b) => (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity));
  }

  const top = mapped.slice(0, maxResults);
  for (const place of top) {
    if (place.placeId) placeCache.set(place.placeId, place);
  }
  return top;
}

/**
 * Fetch full Google details for a place (photo, phone, website, hours, a top
 * review). Used to build the rich card that Routy presents.
 */
export async function getPlaceDetails(placeId: string): Promise<PlaceDetails> {
  const service = await getService();

  const result = await new Promise<google.maps.places.PlaceResult>((resolve, reject) => {
    service.getDetails(
      {
        placeId,
        fields: [
          'place_id',
          'name',
          'formatted_address',
          'formatted_phone_number',
          'rating',
          'user_ratings_total',
          'photos',
          'opening_hours',
          'geometry',
          'url',
          'website',
          'reviews',
        ],
      },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          resolve(place);
        } else {
          reject(new Error(`Place details failed (${status}).`));
        }
      },
    );
  });

  // Surface the highest-rated, longest review as the headline snippet.
  const reviews = result.reviews ?? [];
  const best = [...reviews].sort(
    (a, b) => (b.rating ?? 0) - (a.rating ?? 0) || (b.text?.length ?? 0) - (a.text?.length ?? 0),
  )[0];
  const topReview: PlaceReview | null = best
    ? {
        author: best.author_name || 'Google reviewer',
        rating: typeof best.rating === 'number' ? best.rating : null,
        text: best.text || '',
        relativeTime: best.relative_time_description || '',
      }
    : null;

  return {
    placeId: result.place_id || placeId,
    name: result.name || 'Unknown',
    address: result.formatted_address || '',
    lat: result.geometry?.location?.lat() ?? 0,
    lng: result.geometry?.location?.lng() ?? 0,
    rating: typeof result.rating === 'number' ? result.rating : null,
    reviewCount: typeof result.user_ratings_total === 'number' ? result.user_ratings_total : null,
    openNow: result.opening_hours?.isOpen?.() ?? null,
    phone: result.formatted_phone_number || null,
    website: result.website || null,
    mapsUrl: result.url || null,
    photoUrl: result.photos?.[0]?.getUrl({ maxWidth: 480, maxHeight: 280 }) ?? null,
    topReview,
  };
}
